import React, { useEffect, useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { toast_size_css, toast_show_animation, toast_hide_animation } from './styles';
import { semantic_colors, shapes } from '../../foundation';
import { Stack } from '../stack';
import { IconCircleWarningFillBold, IconCheckBold } from '../icons/generated';
import { ToastSize, ToastStatus } from './types';
import { useProviderConfig } from '../provider';

export interface ToastComponentProps {
  /**
   * id값을 지정할 수 있습니다.
   *  - id값은 unique 해야합니다.
   *  - 해당 id값으로 destroy 수동 처리가 가능합니다.
   *  - 지정하지 않을 경우 messageManager generateMessageId를 통해 자동으로 생성되어 관리됩니다.
   */
  id?: string;
  content: ReactNode;
  /**
   * 지속시간(ms)
   * @default 3000
   */
  duration?: number;
  /** @default 'medium' */
  size?: ToastSize;
  /** @default 'info' */
  status?: ToastStatus;
  destroy: () => void;
}

export const ToastComponent: React.FC<ToastComponentProps> = React.memo(
  ({ content, duration = 3000, size = 'medium', status = 'info', destroy }) => {
    const { locale } = useProviderConfig();
    const [show, setShow] = useState(true);
    const iconSize = 20;

    useEffect(() => {
      const hide_timer = setTimeout(() => {
        setShow(false);
      }, duration);
      return () => clearTimeout(hide_timer);
    }, []);

    useEffect(() => {
      const clear_timer = setTimeout(() => {
        destroy();
      }, duration + 1000);

      return () => clearTimeout(clear_timer);
    }, [duration, destroy]);

    return (
      <Container size={size} visible={show} role='status'>
        <Stack direction='row' gap={8} align='center'>
          {status === 'success' && (
            <IconCheckBold
              size={iconSize}
              color={semantic_colors.state.positive}
              aria-label={locale?.accessibility.info}
            />
          )}
          {status === 'error' && (
            <IconCircleWarningFillBold
              size={iconSize}
              color={semantic_colors.state.negative}
              aria-label={locale?.accessibility.error}
            />
          )}
          {content}
        </Stack>
      </Container>
    );
  },
  (prevProps: ToastComponentProps, nextProps: ToastComponentProps) => {
    return prevProps.id === nextProps.id;
  },
);

const Container = styled('div', {
  shouldForwardProp: (propName) => !['visible'].includes(propName as string),
})<{
  visible: boolean;
  size: ToastSize;
}>`
  flex: none;
  padding: 12px 16px;
  background-color: ${semantic_colors.background.surface_inverse};
  color: ${semantic_colors.content.primary_inverse};
  ${shapes.border_radius.full};
  ${shapes.shadow.medium};
  ${({ size }) => toast_size_css[size]}
  ${({ visible }) =>
    visible
      ? css`
          animation: ${toast_hide_animation} ease-in-out 0.2s forwards;
        `
      : css`
          animation: ${toast_show_animation} ease-in-out 0.15s forwards;
        `};
`;
